import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { sectionReveal, heroReveal, delayedReveal } from "@/lib/motion";
import { ScrollText, Mail, UserCheck, Ban, ShieldAlert, RefreshCw, Scale, CreditCard, FileText, ArrowLeft, AlertTriangle, Siren, Video, Landmark, Copyright } from "lucide-react";
import { Header } from "@/components/Header";
import { AuLawTable } from "@/components/AuLawTable";

import { Footer } from "@/components/Footer";
import { PrintButton } from "@/components/PrintButton";

const auPolice = [
  ["New South Wales", "NSW Police Force — Police Assistance Line 131 444"],
  ["Victoria", "Victoria Police — Police Assistance Line 131 444"],
  ["Queensland", "Queensland Police Service — Policelink 131 444"],
  ["Western Australia", "WA Police Force — 131 444"],
  ["South Australia", "SA Police — 131 444"],
  ["Tasmania", "Tasmania Police — 131 444"],
  ["Australian Capital Territory", "ACT Policing — 131 444"],
  ["Northern Territory", "NT Police — 131 444"],
];

const auRecordingLaws = [
  ["New South Wales", "Surveillance Devices Act 2007"],
  ["Victoria", "Surveillance Devices Act 1999"],
  ["Queensland", "Invasion of Privacy Act 1971"],
  ["Western Australia", "Surveillance Devices Act 1998"],
  ["South Australia", "Surveillance Devices Act 2016"],
  ["Tasmania", "Listening Devices Act 1991"],
  ["Australian Capital Territory", "Listening Devices Act 1992"],
  ["Northern Territory", "Surveillance Devices Act 2007"],
];

const LAST_UPDATED = "June 20, 2026";
const CONTACT_EMAIL = "emergo.support@gmail.com";

const tableOfContents = [
  { id: "acceptance", label: "Acceptance of Terms" },
  { id: "accounts", label: "Your Account" },
  { id: "user-information", label: "Accuracy of Your Information" },
  { id: "roadside-duties", label: "Your Legal Duties After an Incident" },
  { id: "recording-laws", label: "Dashcam and Recording Laws" },
  { id: "acceptable-use", label: "Acceptable Use" },
  { id: "payments", label: "Payments" },
  { id: "intellectual-property", label: "Intellectual Property" },
  { id: "copyright", label: "Copyright" },
  { id: "disclaimer", label: "Disclaimer of Warranties" },
  { id: "liability", label: "Limitation of Liability" },
  { id: "australian-consumer-law", label: "Australian Consumer Law" },
  { id: "termination", label: "Termination" },
  { id: "changes", label: "Changes to These Terms" },
  { id: "contact", label: "Contact Us" },
];

const Section = ({ id, icon: Icon, title, children }) => (
  <motion.section
    id={id}
    data-testid={`section-${id}`}
    {...sectionReveal}
    className="scroll-mt-24"
  >
    <div className="flex items-center gap-3 mb-4">
      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-brand-soft text-brand">
        <Icon className="h-[18px] w-[18px]" strokeWidth={1.75} />
      </span>
      <h2 className="font-extrabold tracking-tight text-2xl md:text-[1.75rem] leading-tight text-ink">{title}</h2>
    </div>
    <div className="pl-0 md:pl-12 space-y-4 text-ink-2 leading-relaxed text-base">{children}</div>
  </motion.section>
);

export default function TermsOfService() {
  return (
    <div className="min-h-screen bg-white text-ink antialiased" data-testid="terms-page">
      <Header />

      <main className="mx-auto max-w-3xl px-6">
        <motion.div
          {...heroReveal}
          className="pt-16 pb-12 border-b border-gray-200"
        >
          <Link to="/" data-testid="back-to-home-link" className="print:hidden inline-flex items-center gap-2 text-sm text-gray-500 hover:text-brand transition-colors mb-8">
            <ArrowLeft className="h-4 w-4" strokeWidth={1.75} />
            Back to home
          </Link>
          <p className="text-xs uppercase tracking-[0.08em] font-bold text-brand mb-4">Legal</p>
          <h1 className="font-extrabold tracking-tight text-4xl sm:text-5xl lg:text-6xl leading-[1.05] text-ink" data-testid="page-title">
            Terms of Service
          </h1>
          <p className="mt-6 text-lg text-ink-2 max-w-xl leading-relaxed">
            These terms govern your use of Emergo. By using the app, you agree to them.
          </p>
          <div className="mt-6 flex flex-wrap items-center gap-4">
            <p className="text-sm text-gray-500" data-testid="last-updated">Last updated: {LAST_UPDATED}</p>
            <PrintButton />
          </div>
        </motion.div>

        <motion.nav
          aria-label="Table of contents"
          data-testid="table-of-contents"
          {...delayedReveal}
          className="mt-12 rounded-2xl border border-gray-200 bg-white p-6 sm:p-8"
        >
          <p className="text-xs uppercase tracking-[0.08em] font-bold text-brand mb-5">On this page</p>
          <ol className="grid gap-x-8 gap-y-1 sm:grid-cols-2">
            {tableOfContents.map((item, i) => (
              <li key={item.id}>
                <a href={`#${item.id}`} data-testid={`toc-link-${item.id}`} className="group flex items-baseline gap-3 rounded-md py-2 text-ink-2 transition-colors hover:text-brand">
                  <span className="font-extrabold tracking-tight text-sm text-gray-400 tabular-nums transition-colors group-hover:text-brand">{String(i + 1).padStart(2, "0")}</span>
                  <span className="text-[15px] underline-offset-4 group-hover:underline decoration-gray-300">{item.label}</span>
                </a>
              </li>
            ))}
          </ol>
        </motion.nav>

        <div className="py-14 space-y-14">
          <Section id="acceptance" icon={FileText} title="Acceptance of Terms">
            <p>
              By accessing or using Emergo ("Emergo", "we", "us", or "our"), you agree to be bound by these Terms of
              Service. If you do not agree to these terms, please do not use the app. These terms apply to all users of
              Emergo worldwide.
            </p>
          </Section>

          <Section id="accounts" icon={UserCheck} title="Your Account">
            <p>
              To use certain features, you may need to create an account. You are responsible for keeping your login
              details secure and for all activity that happens under your account. Please let us know right away if you
              believe your account has been used without your permission.
            </p>
            <p>You agree to provide accurate information and to keep it up to date.</p>
          </Section>

          <Section id="user-information" icon={AlertTriangle} title="Accuracy of Your Information">
            <p>
              You are solely responsible for the accuracy and completeness of any information you enter into Emergo,
              including account details, payment information, and any other content you provide.
            </p>
            <p>
              If you enter incorrect, incomplete, or outdated information, any resulting issues are your responsibility
              and not the fault of Emergo. To the fullest extent permitted by law, Emergo does not accept any
              accountability or liability for any loss, damage, error, or other problem that arises from information
              that was entered incorrectly.
            </p>
          </Section>

          <Section id="roadside-duties" icon={Siren} title="Your Legal Duties After an Incident">
            <p>
              Emergo is a guide to help you record and organise information. It does not replace your legal duties under
              the road rules of your state or territory. Those duties may include stopping, helping anyone who is hurt,
              swapping details with the other parties, and reporting the incident to police.
            </p>
            <p>
              The rules on when you must report a crash to police, and how quickly, differ between states and territories.
              You are responsible for knowing and following the rules where the incident happens. If anyone is injured or
              in danger, call <span className="font-semibold text-ink">000</span> first. For non-urgent reports, contact
              your local police:
            </p>
            <AuLawTable testId="au-police-table" heading="Who to contact" rows={auPolice} />
          </Section>

          <Section id="recording-laws" icon={Video} title="Dashcam and Recording Laws">
            <p>
              Each state and territory has its own law on recording video and audio, especially private conversations.
              You are responsible for making sure any photo, dashcam clip or recording you add to Emergo was made lawfully
              where you were, and for getting consent where the law requires it.
            </p>
            <AuLawTable testId="au-recording-table" rows={auRecordingLaws} />
            <p>
              Only share recordings of other people for a legitimate reason, such as an insurance claim or a police report.
              Emergo is not responsible for recordings you make, upload or share.
            </p>
          </Section>

          <Section id="acceptable-use" icon={Ban} title="Acceptable Use">
            <p>When using Emergo, you agree not to:</p>
            <ul className="list-disc pl-5 space-y-2 marker:text-brand">
              <li>Use the app for any unlawful purpose or in violation of these terms.</li>
              <li>Attempt to disrupt, damage, or gain unauthorized access to the app or its systems.</li>
              <li>Upload content that is illegal, harmful, or infringes the rights of others.</li>
              <li>Misuse other users' information or the content available in the app.</li>
            </ul>
          </Section>

          <Section id="payments" icon={CreditCard} title="Payments">
            <p>
              If you make a payment through Emergo, you agree to provide accurate payment information and authorize us
              to process the transaction. Payment information is handled only within the app and is never sold or shared.
              Fees, where they apply, will be made clear to you before you are charged.
            </p>
          </Section>

          <Section id="intellectual-property" icon={Scale} title="Intellectual Property">
            <p>
              The Emergo app, including its design, features, and content we provide, belongs to us and is protected by
              applicable laws. You may use the app for its intended purpose, but you may not copy, resell, or
              redistribute it without our permission. Content you add to the app remains yours.
            </p>
          </Section>

          <Section id="copyright" icon={Copyright} title="Copyright">
            <p>
              <span className="font-semibold text-ink" data-testid="terms-copyright-line">Emergo © {new Date().getFullYear()} Laura Hockenhull</span>{" "}
              (ABN 34 228 148 692). All rights reserved. The Emergo app, website, name, logo, design, text, graphics and
              software are owned by Laura Hockenhull or her licensors. They are protected by the{" "}
              <em>Copyright Act 1968</em> (Cth) and other intellectual property laws.
            </p>
            <p>
              Apart from the uses the law allows, such as fair dealing, you may not copy, change, publish, sell or share any
              part of Emergo without written permission from Laura Hockenhull.
            </p>
            <p className="font-medium text-ink">Your content</p>
            <p>
              You keep ownership of everything you add to Emergo, including photos, dashcam clips, sketches, notes and
              reports. You give Emergo a limited, non-exclusive licence to store, process and display that content only so
              we can run the app for you, for example to create your PDF reports or share them when you ask us to. This
              licence ends when you delete the content or your account. You confirm that you own, or have the right to use,
              anything you upload.
            </p>
            <p className="font-medium text-ink">Reporting copyright infringement</p>
            <p>
              If you believe content in Emergo infringes your copyright, email us at{" "}
              <a href={`mailto:${CONTACT_EMAIL}`} className="font-medium text-ink underline underline-offset-4 decoration-gray-300 hover:decoration-brand transition-colors" data-testid="copyright-report-link">{CONTACT_EMAIL}</a>{" "}
              with:
            </p>
            <ul className="list-disc pl-5 space-y-2 marker:text-brand">
              <li>your name and contact details;</li>
              <li>a description of the work you own;</li>
              <li>where the infringing content appears; and</li>
              <li>a statement that the information is accurate and that you are the owner or are authorised to act for them.</li>
            </ul>
            <p>We will review every report and remove content that infringes copyright.</p>
          </Section>

          <Section id="disclaimer" icon={ShieldAlert} title="Disclaimer of Warranties">
            <p>
              Emergo is provided "as is" and "as available". While we work hard to keep the app reliable and secure, we
              do not guarantee that it will always be available, error-free, or meet every expectation. Your use of the
              app is at your own discretion.
            </p>
          </Section>

          <Section id="liability" icon={Scale} title="Limitation of Liability">
            <p>
              To the extent permitted by law, Emergo will not be liable for any indirect, incidental, or consequential
              damages arising from your use of, or inability to use, the app. Nothing in these terms limits any rights
              you may have that cannot be excluded under applicable law.
            </p>
          </Section>

          <Section id="australian-consumer-law" icon={Landmark} title="Australian Consumer Law">
            <p>
              Emergo (ABN: <span className="font-semibold text-ink" data-testid="terms-abn">34 228 148 692</span>)
              provides its services with guarantees under the Australian Consumer Law (Schedule 2 of the{" "}
              <em>Competition and Consumer Act 2010</em> (Cth)) that cannot be excluded. These guarantees apply in every
              state and territory.
            </p>
            <p>
              Nothing in these terms excludes, restricts or changes those guarantees or any other right you have under the
              law. This includes the Accuracy of Your Information, Disclaimer of Warranties and Limitation of Liability
              sections. Where the law allows us to limit our liability for a failure to meet a guarantee, our liability is
              limited to supplying the service again or paying the cost of having it supplied again.
            </p>
            <ul className="list-disc pl-5 space-y-2 marker:text-brand">
              <li>Prices are shown in Australian dollars and include GST where it applies.</li>
              <li>Subscriptions renew automatically until you cancel. You can cancel at any time, and you keep access until the end of the period you have paid for.</li>
              <li>You can contact your state or territory consumer protection agency or the ACCC for independent advice about your consumer rights.</li>
            </ul>
            <p>
              These terms are governed by the laws in force in Australia. You can bring a claim in the courts of the state or
              territory where you live.
            </p>
          </Section>

          <Section id="termination" icon={Ban} title="Termination">
            <p>
              You may stop using Emergo at any time. We may suspend or end your access if you break these terms or use
              the app in a way that could harm other users or the service. You can ask us to delete your account and
              data at any time.
            </p>
          </Section>

          <Section id="changes" icon={RefreshCw} title="Changes to These Terms">
            <p>
              We may update these Terms of Service from time to time. When we make changes, we will revise the "last
              updated" date at the top of this page. Continuing to use Emergo after changes take effect means you accept
              the updated terms.
            </p>
          </Section>

          <Section id="contact" icon={Mail} title="Contact Us">
            <p>If you have any questions about these Terms of Service, please contact us at:</p>
            <a
              href={`mailto:${CONTACT_EMAIL}`}
              data-testid="contact-email-link"
              className="inline-flex items-center gap-3 rounded-xl border border-gray-200 bg-white px-5 py-4 text-ink transition-colors hover:border-brand group"
            >
              <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-brand-soft text-brand transition-transform group-hover:scale-105">
                <Mail className="h-[18px] w-[18px]" strokeWidth={1.75} />
              </span>
              <span className="font-medium">{CONTACT_EMAIL}</span>
            </a>
          </Section>
        </div>
      </main>
      <Footer />
    </div>
  );
}
